import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';

// Promoter Browse (SHARC_Spec_Promoter_Browse_2026-10-07) — brought up to
// ArtistList.js's level: name/city search, Most Recent Show range, Country /
// State dropdowns, sortable columns, mobile cards + sort row, and
// FestivalList.js's jump-to-page pagination.

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/['‘’‛`\-.,:!?&]/g, '')
        .toLowerCase();

const PAGE_SIZE = 25;

const NUMERIC_COLS = new Set(['total_shows', 'festival_count']);
const DATE_COLS    = new Set(['last_show_date']);

// Shared between the desktop table headers and the mobile "Sort by" select,
// so both stay in sync automatically if columns ever change.
const SORT_COLUMNS = [
    { col: 'name',           label: 'Promoter' },
    { col: 'city',           label: 'City' },
    { col: 'state',          label: 'State' },
    { col: 'country',        label: 'Country' },
    { col: 'total_shows',    label: '# of Shows' },
    { col: 'festival_count', label: '# of Festivals' },
    { col: 'last_show_date', label: 'Most Recent Show' },
];

function formatShowDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    });
}

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

// ── Jump-to-page pagination (local copy of FestivalDetail.js's Pagination —
// copy-per-file convention, see context doc Section 12) ────────────────────
function JumpPagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={S.pageJumpWrap}>
            <span style={S.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={S.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={S.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

// ── Mobile card (replaces the table below 768px) ───────────────────────────
function PromoterCardMobile({ promoter }) {
    const location = [promoter.city, promoter.state, promoter.country].filter(Boolean).join(', ');
    const shows = Number(promoter.total_shows) || 0;
    const festivals = Number(promoter.festival_count) || 0;
    return (
        <Link to={`/promoters/${promoter.slug || promoter.id}`} style={mobileStyles.card}>
            <div style={mobileStyles.cardName}>{promoter.name}</div>
            {location && <div style={mobileStyles.cardLocation}>{location}</div>}
            <div style={mobileStyles.cardStatsRow}>
                <span>{plural(shows, 'show')} · {plural(festivals, 'festival')}</span>
                <span style={mobileStyles.cardDate}>Most Recent Show: {formatShowDate(promoter.last_show_date)}</span>
            </div>
        </Link>
    );
}

function PromoterList() {
    const isMobile = useIsMobile();
    const [promoters, setPromoters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Picklists for code→name lookup in filter dropdowns
    const [picklists, setPicklists] = useState(null);

    // Filter state
    const [search, setSearch]               = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterState, setFilterState]     = useState('');
    const [dateFrom, setDateFrom]           = useState('');
    const [dateTo, setDateTo]               = useState('');

    // Sort state — default Promoter A–Z (spec §3.5)
    const [sortCol, setSortCol] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage]       = useState(1);

    useEffect(() => {
        document.title = 'SHARC - Promoters';
        Promise.all([
            axios.get(`${config.API_URL}/promoters`),
            axios.get(`${config.API_URL}/picklists`)
        ])
            .then(([promotersRes, picklistsRes]) => {
                setPromoters(promotersRes.data.promoters || []);
                setPicklists(picklistsRes.data);
                setLoading(false);
            })
            .catch(() => { setError('Failed to load promoters'); setLoading(false); });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    // code→name lookup maps built from picklists
    const countryNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.countries.map(c => [c.code, c.name]));
    }, [picklists]);

    // Key: "countryId|stateCode" — avoids collisions where two countries share a code (e.g. CO)
    const stateNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.states_regions.map(s => [`${s.country_id}|${s.code}`, s.name]));
    }, [picklists]);

    // Resolve the picklist country_id for the currently selected country code
    const selectedCountryPicklistId = useMemo(() => {
        if (!picklists || !filterCountry) return null;
        const match = picklists.countries.find(c => c.code === filterCountry);
        return match ? match.id : null;
    }, [picklists, filterCountry]);

    // Build unique filter options from data (still using codes as values)
    const countries = useMemo(() => {
        const unique = [...new Set(promoters.map(p => p.country).filter(Boolean))];
        return unique.sort((a, b) => (countryNameMap[a] || a).localeCompare(countryNameMap[b] || b));
    }, [promoters, countryNameMap]);
    const states = useMemo(() => {
        const src = filterCountry
            ? promoters.filter(p => p.country === filterCountry)
            : promoters;
        return [...new Set(src.map(p => p.state).filter(Boolean))].sort();
    }, [promoters, filterCountry]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    };

    const filtered = useMemo(() => {
        const q = normalizeStr(search);
        return promoters.filter(p => {
            if (search && !((p.name && normalizeStr(p.name).includes(q)) || (p.city && normalizeStr(p.city).includes(q)))) return false;
            if (filterCountry && p.country !== filterCountry) return false;
            if (filterState   && p.state !== filterState)     return false;
            if (dateFrom && (!p.last_show_date || p.last_show_date.slice(0, 10) < dateFrom)) return false;
            if (dateTo   && (!p.last_show_date || p.last_show_date.slice(0, 10) > dateTo))   return false;
            return true;
        });
    }, [promoters, search, filterCountry, filterState, dateFrom, dateTo]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const av = NUMERIC_COLS.has(sortCol) ? Number(a[sortCol] ?? 0) : (a[sortCol] ?? '');
            const bv = NUMERIC_COLS.has(sortCol) ? Number(b[sortCol] ?? 0) : (b[sortCol] ?? '');
            const cmp = NUMERIC_COLS.has(sortCol)
                ? av - bv
                : DATE_COLS.has(sortCol)
                    ? (av || '').localeCompare(bv || '')
                    : String(av).localeCompare(String(bv));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [filtered, sortCol, sortDir]);

    const clearFilters = () => {
        setSearch('');
        setFilterCountry(''); setFilterState('');
        setDateFrom(''); setDateTo('');
        setPage(1);
    };

    const hasFilters = search || filterCountry || filterState || dateFrom || dateTo;

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span style={S.sortIcon}>↕</span>;
        return <span style={{ ...S.sortIcon, color: '#bb86fc' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    if (loading) return <div style={S.message}>Loading promoters...</div>;
    if (error)   return <div style={S.errMsg}>{error}</div>;

    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            {/* ── Header ── */}
            <div style={isMobile ? { ...S.header, ...Smobile.header } : S.header}>
                <h1 style={S.title}>Promoters</h1>
                <div style={S.headerRight}>
                    <span style={S.count}>{sorted.length.toLocaleString()} of {promoters.length.toLocaleString()}</span>
                    <Link to="/add-promoter" style={S.addButton}>+ Add Promoter</Link>
                </div>
            </div>

            {/* ── Filters Row 1: text search ── */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by promoter name or city..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    style={S.searchInput}
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* ── Filters Row 2: Most Recent Show range + Country / State ── */}
            <div style={S.filterBar}>
                <span style={S.dateLabel}>Most Recent Show</span>
                <input
                    type="date"
                    value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                    style={S.dateInput}
                />
                <span style={S.capDash}>–</span>
                <input
                    type="date"
                    value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setPage(1); }}
                    style={S.dateInput}
                />
                <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setFilterState(''); setPage(1); }} style={isMobile ? { ...S.filterSelect, ...Smobile.filterSelect } : S.filterSelect}>
                    <option value="">All Countries</option>
                    {countries.map(c => (
                        <option key={c} value={c}>{countryNameMap[c] || c}</option>
                    ))}
                </select>
                <select value={filterState} onChange={e => { setFilterState(e.target.value); setPage(1); }} style={isMobile ? { ...S.filterSelect, ...Smobile.filterSelect } : S.filterSelect} disabled={states.length === 0}>
                    <option value="">All States</option>
                    {states.map(s => (
                        <option key={s} value={s}>{stateNameMap[`${selectedCountryPicklistId}|${s}`] || s}</option>
                    ))}
                </select>
            </div>

            {/* ── Mobile-only sort control (table headers below handle this on desktop) ── */}
            {isMobile && (
                <div style={mobileStyles.sortRow}>
                    <select
                        value={sortCol}
                        onChange={e => { setSortCol(e.target.value); setPage(1); }}
                        style={mobileStyles.sortSelect}
                    >
                        {SORT_COLUMNS.map(({ col, label }) => (
                            <option key={col} value={col}>Sort: {label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                        style={mobileStyles.sortDirBtn}
                        aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
                    </button>
                </div>
            )}

            {sorted.length === 0 ? (
                <div style={S.message}>
                    {hasFilters ? 'No promoters match your filters.' : 'No promoters yet.'}
                </div>
            ) : isMobile ? (
                <div style={mobileStyles.cardList}>
                    {pageItems.map((p) => (
                        <PromoterCardMobile key={p.id} promoter={p} />
                    ))}
                </div>
            ) : (
                <div style={S.tableContainer}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                {SORT_COLUMNS.map(({ col, label }) => (
                                    <th
                                        key={col}
                                        style={{ ...S.th, ...(NUMERIC_COLS.has(col) ? S.thNum : {}), cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort(col)}
                                    >
                                        {label} <SortIcon col={col} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((p, idx) => (
                                <tr
                                    key={p.id}
                                    style={idx % 2 === 0 ? S.trEven : S.trOdd}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#252525'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#1e1e1e' : '#1a1a1a'}
                                >
                                    <td style={S.td}>
                                        <Link to={`/promoters/${p.slug || p.id}`} style={S.link}>{p.name}</Link>
                                    </td>
                                    <td style={S.td}>{p.city || '—'}</td>
                                    <td style={S.td}>{p.state || '—'}</td>
                                    <td style={S.td}>{p.country || '—'}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(p.total_shows) || 0}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(p.festival_count) || 0}</td>
                                    <td style={S.td}>{formatShowDate(p.last_show_date)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <JumpPagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} />
        </div>
    );
}

const S = {
    header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:       { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
    count:       { fontSize: '13px', color: '#666' },
    addButton:   { backgroundColor: '#bb86fc', color: '#121212', textDecoration: 'none', padding: '8px 18px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px' },

    filterBar:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
    searchInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 260px', minWidth: '200px', boxSizing: 'border-box' },
    // Country / State grow to fill the rest of row 2, so its right edge lines
    // up flush with the full-width search box on row 1.
    filterSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', color: '#e0e0e0', flex: '1 1 180px', minWidth: '110px', boxSizing: 'border-box' },
    clearBtn:     { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

    tableContainer: { overflowX: 'auto' },
    table:          { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th:             { textAlign: 'left', padding: '10px 14px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    thNum:          { textAlign: 'right' },
    td:             { padding: '10px 14px', color: '#e0e0e0', borderBottom: '1px solid #222', whiteSpace: 'nowrap' },
    trEven:         { backgroundColor: '#1e1e1e' },
    trOdd:          { backgroundColor: '#1a1a1a' },
    link:           { color: '#bb86fc', textDecoration: 'none' },
    numCell:        { textAlign: 'right', color: '#aaa', fontVariantNumeric: 'tabular-nums' },
    dateLabel:  { fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
    dateInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#e0e0e0', width: '130px', boxSizing: 'border-box' },
    capDash:    { color: '#555', fontSize: '14px' },
    sortIcon:   { fontSize: '10px', marginLeft: '4px', color: '#555' },
    message:        { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errMsg:         { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // Jump-to-page pagination — copied from FestivalDetail.js / FestivalList.js
    pageJumpWrap:   { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel:  { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },
};

// ── Mobile style overrides (same pattern as SHARC_Mobile_Responsive_Spec_v1.1) ──
const Smobile = {
    // "Promoters" heading + count/+Add group can overflow a nowrap flex row on
    // narrow viewports — allow it to wrap onto a second line instead.
    header: {
        flexWrap: 'wrap',
        gap: '10px',
    },
    // On a phone the two dropdowns share the row evenly instead of the
    // desktop width cap.
    filterSelect: {
        flex: '1 1 140px',
    },
};

// Kept as a separate object, only used when useIsMobile() is true, so the
// desktop table above is completely untouched.
const mobileStyles = {
    sortRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
    sortSelect: {
        flex: '1 1 auto',
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 12px',
        fontSize: '13px',
        color: '#e0e0e0',
        boxSizing: 'border-box',
    },
    sortDirBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 14px',
        fontSize: '13px',
        color: '#e0e0e0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        display: 'block',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '14px 16px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    cardName: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#bb86fc',
        marginBottom: '4px',
    },
    cardLocation: {
        fontSize: '13px',
        color: '#ccc',
        marginBottom: '8px',
    },
    cardStatsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 12px',
        fontSize: '12px',
        color: '#aaa',
        paddingTop: '8px',
        borderTop: '1px solid #2a2a2a',
    },
    // Most Recent Show always takes its own line.
    cardDate: {
        flexBasis: '100%',
    },
};

export default PromoterList;
